import React, { useState, useEffect } from 'react'
import { Container, Row, Col, Card, CardBody, CardHeader, CardFooter, Form, FormGroup, Label, Input, Button } from 'reactstrap'
import { Breadcrumbs, H4 } from '../../AbstractElements'
import { useAuth } from '../../_helper/Auth/AuthProvider'
import { useNavigate } from 'react-router-dom'
import { updateData, getIdData as getAdminOrParentData } from '../../lib/profiles'
import { getIdData as getStudentData } from '../../lib/studentinfo'
import { getIdData as getInstructorData, updateData as updateInstructorData } from '../../lib/instructorinfo'
import { uploadFile, getFile, removeFile } from '../../lib/file'
import { ToastNotification } from '../Lessons/CourseReport/CourseReportComponents'

const ProfileEdit = () => {
	const { user } = useAuth()
	const navigate = useNavigate()
	const [formData, setFormData] = useState({})
	const [selectedImage, setSelectedImage] = useState(null)
	const [avatar, setAvatar] = useState('')
	const [oldAvatarUrl, setOldAvatarUrl] = useState('')
	const [loading, setLoading] = useState(true)
	const [saving, setSaving] = useState(false)
	const [isDarkMode, setIsDarkMode] = useState(false)
	const [toastMessage, setToastMessage] = useState('')
	const [showToast, setShowToast] = useState(false)

	const showToastMessage = (message) => {
		setToastMessage(message)
		setShowToast(true)
		setTimeout(() => setShowToast(false), 3000)
	}

	useEffect(() => {
		const fetchProfileData = async () => {
			if (!user) return

			try {
				let info
				if (user.role === 'Student911' || user.role === 'Student12') {
					info = await getStudentData(user.id)
				} else if (user.role === 'Instructor') {
					info = await getInstructorData(user.id)
				} else if (user.role === 'Admin' || user.role === 'Parent') {
					info = await getAdminOrParentData(user.id)
				}

				if (info && info.length > 0) {
					const data = info[0]
					setFormData({
						name: data.name || '',
						surname: data.surname || '',
						phone: data.phone || '',
						email: data.email || '',
						username: data.username || '',
						class: data.class || '',
						studentschool: data.studentschool || '',
						graduateyear: data.graduateyear || '',
						gpa: data.gpa || '',
						satpsat: data.satpsat || '',
						statetest: data.statetest || '',
						major: data.major || '',
						career: data.career || '',
						fiveAdjectives: data.fiveAdjectives || '',
						hsSchoolStatistics: data.hsSchoolStatistics || '',
						topThreeColleges: data.topThreeColleges || '',
						lor: data.lor || '',
						activities: data.activities || '',
						collegeEssay: data.collegeEssay || '',
						instructorNote: data.instructorNote || '',
						studentNote: data.studentNote || '',
						meetlink: data.meetlink || '',
						folderlink: data.folderlink || ''
					})

					// Store old avatar URL for cleanup
					if (data.avatar_url) {
						setOldAvatarUrl(data.avatar_url)
						try {
							const avatarData = await getFile(data.avatar_url)
							setAvatar(avatarData.publicUrl)
						} catch (error) {
							console.error('Error fetching avatar:', error)
						}
					} else {
						setOldAvatarUrl('')
					}
				}
			} catch (error) {
				console.error('Error fetching profile data:', error)
			} finally {
				setLoading(false)
			}
		}

		fetchProfileData()
	}, [user])

	useEffect(() => {
		const checkDarkMode = () => {
			setIsDarkMode(document.body.classList.contains('dark-only'))
		}
		checkDarkMode()
		const observer = new MutationObserver(checkDarkMode)
		observer.observe(document.body, { attributes: true, attributeFilter: ['class'] })
		return () => observer.disconnect()
	}, [])

	const handleInputChange = (e) => {
		const { name, value } = e.target
		setFormData((prev) => ({
			...prev,
			[name]: value
		}))
	}

	const handleImageChange = (e) => {
		const file = e.target.files[0]
		if (file) {
			setSelectedImage(file)
		}
	}

	const handleImageUpload = async () => {
		if (!selectedImage || !user) return

		try {
			const fileExt = selectedImage.name.split('.').pop()
			const newFileName = `${user.id}.${fileExt}`

			// Upload new image
			await uploadFile(newFileName, selectedImage)

			// Update profile with new avatar - use correct updateData based on role
			const updatePayload = {
				id: user.id,
				avatar_url: newFileName
			}

			// Use the correct updateData function based on user role
			// Students and Admin/Parent use profiles table, Instructor uses instructorinfoview
			if (user.role === 'Student911' || user.role === 'Student12' || user.role === 'Admin' || user.role === 'Parent') {
				await updateData(updatePayload)
			} else if (user.role === 'Instructor') {
				await updateInstructorData(updatePayload)
			}

			// Remove old avatar if exists
			if (oldAvatarUrl && oldAvatarUrl !== newFileName) {
				try {
					await removeFile(oldAvatarUrl)
				} catch (error) {
					console.error('Error removing old avatar:', error)
				}
			}

			// Update avatar state with new image
			try {
				const avatarData = await getFile(newFileName)
				setAvatar(avatarData.publicUrl)
				setOldAvatarUrl(newFileName)
			} catch (error) {
				console.error('Error fetching new avatar:', error)
			}

			setSelectedImage(null)
			showToastMessage('Avatar updated successfully!')
		} catch (error) {
			console.error('Error updating avatar:', error)
			showToastMessage('Failed to update avatar')
		}
	}

	const handleSubmit = async (e) => {
		e.preventDefault()
		setSaving(true)

		try {
			// Get the fields for the current role
			const roleFields = getFields()

			// Filter formData to only include fields that exist for this role
			const filteredData = {}
			roleFields.forEach((field) => {
				if (formData[field.name] !== undefined && formData[field.name] !== '') {
					filteredData[field.name] = formData[field.name]
				}
			})

			const updatePayload = {
				id: user.id,
				...filteredData
			}

			// Use the correct updateData function based on user role
			// Students and Admin/Parent use profiles table, Instructor uses instructorinfoview
			if (user.role === 'Student911' || user.role === 'Student12' || user.role === 'Admin' || user.role === 'Parent') {
				await updateData(updatePayload)
			} else if (user.role === 'Instructor') {
				await updateInstructorData(updatePayload)
			}

			showToastMessage('Profile updated successfully!')
			setTimeout(() => navigate(-1), 2000)
		} catch (error) {
			console.error('Error updating profile:', error)
			showToastMessage('Failed to update profile')
		} finally {
			setSaving(false)
		}
	}

	const getStudentFields = () => [
		{ name: 'name', label: 'First Name', type: 'text', readOnly: true },
		{ name: 'surname', label: 'Last Name', type: 'text', readOnly: true },
		{ name: 'username', label: 'Nick Name', type: 'text' },
		{ name: 'class', label: 'Grade Level', type: 'number' },
		{ name: 'phone', label: 'Phone', type: 'tel' },
		{ name: 'studentschool', label: 'School Name', type: 'text' },
		{ name: 'graduateyear', label: 'Graduation Year', type: 'text' },
		{ name: 'gpa', label: 'GPA', type: 'text' },
		{ name: 'satpsat', label: 'SAT/PSAT Scores', type: 'textarea' },
		{ name: 'statetest', label: 'Practice SAT', type: 'textarea' },
		{ name: 'major', label: 'Major', type: 'text' },
		{ name: 'career', label: 'Career', type: 'text' },
		{ name: 'fiveAdjectives', label: 'Five Adjectives', type: 'text' },
		{ name: 'hsSchoolStatistics', label: 'High School Statistics', type: 'text' },
		{ name: 'topThreeColleges', label: 'Top Three Colleges', type: 'text' },
		{ name: 'lor', label: 'Letter of Recommendation', type: 'text' },
		{ name: 'activities', label: 'Activities', type: 'textarea' },
		{ name: 'collegeEssay', label: 'College Essay Progress', type: 'textarea' },
		{ name: 'instructorNote', label: "Counselor's Comment", type: 'textarea' },
		{ name: 'studentNote', label: "Student's Comment", type: 'textarea' },
		{ name: 'meetlink', label: 'Zoom Link', type: 'url' },
		{ name: 'folderlink', label: 'Folder Link', type: 'url' }
	]

	const getInstructorFields = () => [
		{ name: 'name', label: 'First Name', type: 'text', required: true },
		{ name: 'surname', label: 'Last Name', type: 'text', required: true },
		{ name: 'phone', label: 'Phone', type: 'tel' },
		{ name: 'username', label: 'Nick Name', type: 'text' }
	]

	const getAdminFields = () => [
		{ name: 'name', label: 'First Name', type: 'text' },
		{ name: 'surname', label: 'Last Name', type: 'text' },
		{ name: 'phone', label: 'Phone', type: 'tel' },
		{ name: 'email', label: 'Email', type: 'email' }
	]

	const getFields = () => {
		if (user?.role === 'Student911' || user?.role === 'Student12') {
			return getStudentFields()
		} else if (user?.role === 'Instructor' || user?.role === 'Parent') {
			return getInstructorFields()
		} else if (user?.role === 'Admin') {
			return getAdminFields()
		}
	}

	if (!user) {
		return (
			<>
				<Breadcrumbs mainTitle="Edit Profile" parent="Profile" title="Edit Profile" />
				<Container fluid={true}>
					<Row>
						<Col sm="12">
							<Card>
								<CardBody>
									<div className="text-center">
										<div className="alert alert-warning" role="alert">
											Please log in to edit your profile.
										</div>
									</div>
								</CardBody>
							</Card>
						</Col>
					</Row>
				</Container>
			</>
		)
	}

	if (loading) {
		return (
			<>
				<Breadcrumbs mainTitle="Edit Profile" parent="Profile" title="Edit Profile" />
				<Container fluid={true}>
					<Row>
						<Col sm="12">
							<Card>
								<CardBody>
									<div className="text-center">
										<div className="spinner-border text-primary" role="status">
											<span className="sr-only">Loading...</span>
										</div>
									</div>
								</CardBody>
							</Card>
						</Col>
					</Row>
				</Container>
			</>
		)
	}

	return (
		<div className="position-relative">
			<ToastNotification show={showToast} message={toastMessage} onClose={() => setShowToast(false)} />
			<Breadcrumbs mainTitle="Edit Profile" parent="Profile" title="Edit Profile" />
			<Container fluid={true}>
				<Row>
					<Col sm="12">
						<Form onSubmit={handleSubmit}>
							<Card style={{ backgroundColor: isDarkMode ? '#2a2d35' : '#fff' }}>
								<CardHeader className="bg-primary text-white">
									<H4 attrH4={{ className: 'card-title mb-2 text-white' }}>
										<i className="fa fa-user-edit me-2"></i>
										Edit Profile Details
									</H4>
									<p className="mb-0 text-white-50">You have full control to manage your own account settings.</p>
								</CardHeader>
								<CardBody style={{ backgroundColor: isDarkMode ? '#2a2d35' : '#fff' }}>
									{/* Avatar Section */}
									<div className="course-group mb-4 p-4 border rounded" style={{ backgroundColor: isDarkMode ? '#2a2d35' : '#f8f9fa' }}>
										<div className="d-flex flex-column flex-md-row align-items-center justify-content-between">
											<div className="d-flex align-items-center mb-3 mb-md-0">
												<div className="position-relative">
													<img
														src={selectedImage ? URL.createObjectURL(selectedImage) : avatar || require('../../assets/images/user/user-dp.png')}
														alt="Profile Avatar"
														className="img-fluid rounded-circle border border-3 border-white shadow"
														style={{
															width: '120px',
															height: '120px',
															objectFit: 'cover',
															transition: 'all 0.3s ease'
														}}
													/>
													{selectedImage && (
														<div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-50 rounded-circle">
															<i className="fa fa-check text-white fs-4"></i>
														</div>
													)}
												</div>
												<div className="ms-4">
													<h4 className="mb-2" style={{ color: isDarkMode ? '#fff' : '#7366ff' }}>Profile Picture</h4>
													<p className="mb-1" style={{ color: isDarkMode ? '#adb5bd' : '#6c757d' }}>Upload a new profile picture</p>
													<small style={{ color: isDarkMode ? '#adb5bd' : '#6c757d' }}>
														<i className="fa fa-info-circle me-1"></i>
														PNG or JPG, max 800x800 pixels
													</small>
												</div>
											</div>
											<div className="d-flex flex-column flex-sm-row gap-2">
												<label className="btn btn-outline-primary btn-lg px-4 py-2">
													<i className="fa fa-upload me-2"></i>
													Select Photo
													<input type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} />
												</label>
												{selectedImage && (
													<Button color="success" className="btn-lg px-4 py-2" onClick={handleImageUpload} style={{ minWidth: '120px' }}>
														<i className="fa fa-save me-2"></i>
														Update
													</Button>
												)}
											</div>
										</div>
									</div>

									{/* Profile Form */}
									<div className="row">
										{getFields().map((field, index) => (
											<div key={index} className={field.type === 'textarea' ? 'col-12' : 'col-lg-6 col-md-6 col-sm-12'}>
												<FormGroup className="mb-3">
													<Label className="form-control-label fw-bold mb-2" style={{ color: isDarkMode ? '#fff' : 'inherit' }}>
														{field.label}
														{field.required && <span className="text-danger ms-1">*</span>}
													</Label>
													{field.type === 'textarea' ? (
														<Input
															type="textarea"
															name={field.name}
															value={formData[field.name] || ''}
															onChange={handleInputChange}
															className="form-control border-radius-10"
															placeholder={`Enter ${field.label.toLowerCase()}`}
															readOnly={field.readOnly}
															required={field.required}
															rows="4"
															style={{
																resize: 'vertical',
																minHeight: '100px',
																border: '1px solid #e9ecef',
																boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
																backgroundColor: isDarkMode ? '#2a2d35' : '#fff',
																color: isDarkMode ? '#fff' : 'inherit'
															}}
														/>
													) : (
														<Input
															type={field.type}
															name={field.name}
															value={formData[field.name] || ''}
															onChange={handleInputChange}
															className="form-control border-radius-10"
															placeholder={`Enter ${field.label.toLowerCase()}`}
															readOnly={field.readOnly}
															required={field.required}
															style={{
																border: '1px solid #e9ecef',
																boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
																height: '45px',
																padding: '10px 15px',
																backgroundColor: isDarkMode ? '#2a2d35' : '#fff',
																color: isDarkMode ? '#fff' : 'inherit'
															}}
														/>
													)}
													{field.readOnly && (
														<small className="mt-1 d-block" style={{ color: isDarkMode ? '#adb5bd' : '#6c757d' }}>
															<i className="fa fa-info-circle me-1"></i>
															This field cannot be edited
														</small>
													)}
												</FormGroup>
											</div>
										))}
									</div>
								</CardBody>
								<CardFooter className="border-top" style={{ backgroundColor: isDarkMode ? '#2a2d35' : '#f8f9fa' }}>
									<div className="d-flex justify-content-end gap-3">
										<Button color="secondary" className="px-4 py-2" onClick={() => navigate(-1)} style={{ minWidth: '100px' }}>
											<i className="fa fa-times me-2"></i>
											Cancel
										</Button>
										<Button color="primary" type="submit" disabled={saving} className="px-4 py-2" style={{ minWidth: '120px' }}>
											{saving ? (
												<>
													<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
													Saving...
												</>
											) : (
												<>
													<i className="fa fa-save me-2"></i>
													Save Changes
												</>
											)}
										</Button>
									</div>
								</CardFooter>
							</Card>
						</Form>
					</Col>
				</Row>
			</Container>
		</div>
	)
}

export { ProfileEdit }
